/**
 * Compose & schedule. Exported for `Compose.test.tsx` (#249) — which body mode
 * the submit path actually reads is a property of this component.
 */
import { useEffect, useState } from "react";
import { useAsync } from "../useAsync.js";
import { idProblem, isValidId } from "../ids.js";
import { api, type NewsletterPolicy, type ScheduleWhen } from "../api.js";
import { schemas } from "@addressium/core";
import {
  escapeHtml,
  describeSchedule,
  buildEventBridgeCron,
  getNextRuns,
  DAYS_OF_WEEK,
  type DayOfWeek,
  type RecurringFrequency,
  adPositions,
  type AdPositions,
} from "@addressium/domain";

/**
 * Zones offered beside the org default (#355). New York and Chicago were
 * missing, so an Eastern-time org could only get Eastern time by knowing the
 * "Org default" label was wrong.
 */
const ZONE_OPTIONS: Array<{ zone: string; label: string }> = [
  { zone: "America/New_York", label: "Eastern Time (America/New_York)" },
  { zone: "America/Chicago", label: "Central Time (America/Chicago)" },
  { zone: "America/Denver", label: "Mountain Time (America/Denver)" },
  { zone: "America/Los_Angeles", label: "Pacific Time (America/Los_Angeles)" },
];

export function Compose({
  org,
  onScheduled,
  duplicateOf,
  editSeries,
}: {
  org: string;
  onScheduled: () => void;
  /** Prefill from an existing campaign's stored body (#307). */
  duplicateOf?: string;
  editSeries?: string;
}) {
  const lists = useAsync(() => api.lists(org), [org]);
  const templates = useAsync(() => api.templates(org), [org]);
  const segments = useAsync(() => api.segments(org), [org]);
  const feeds = useAsync(() => api.feeds(org), [org]);
  const orgMeta = useAsync(() => api.orgMeta(org), [org]);
  const [listId, setListId] = useState("");
  const [segmentId, setSegmentId] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [subject, setSubject] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [html, setHtml] = useState("");
  const positions = adPositions(html);
  const [when, setWhen] = useState<"now" | "at" | "recurring">("now");
  const [at, setAt] = useState("");
  const [cron, setCron] = useState("cron(0 13 * * ? *)");
  const [timezone, setTimezone] = useState("");
  const [feedId, setFeedId] = useState("");
  const [newsletter, setNewsletter] = useState<import("../api.js").NewsletterPolicy>({failurePolicy:"halt",unchangedPolicy:"hold",subjectMode:"lead",bodySlots:[]});
  const [expectedVersion,setExpectedVersion] = useState<number | undefined>(undefined);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testMsg, setTestMsg] = useState("");
  const [testBusy, setTestBusy] = useState(false);

  const [frequency, setFrequency] = useState<RecurringFrequency>("daily");
  const [selectedDays, setSelectedDays] = useState<DayOfWeek[]>([]);
  const [timeOfDay, setTimeOfDay] = useState("13:00");
  // The zone the runs were computed in travels with them: switching the zone
  // after pressing Preview must not relabel old instants in the new zone.
  const [nextRuns, setNextRuns] = useState<{ runs: Date[]; zone: string } | null>(null);

  useEffect(() => {
    const nextCron = buildEventBridgeCron({
      frequency,
      daysOfWeek: selectedDays,
      timeOfDay,
    });
    setCron(nextCron);
  }, [frequency, selectedDays, timeOfDay]);

  useEffect(() => {
    if (lists.data && lists.data.length > 0 && !listId) setListId(lists.data[0]!.listId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lists.data]);

  /**
   * Prefill from an existing campaign (#307).
   *
   * Everything EXCEPT the campaign id, which must be new — re-using it would
   * overwrite the original's record rather than create a copy. The operator
   * names the duplicate themselves, which also makes it obvious that this is a
   * new send rather than an edit of the old one.
   *
   * The body mode comes from `editorSource` rather than being inferred from the
   * template: an MJML campaign arrives as compiled html plus its source, and
   * inferring would drop them into the Raw HTML editor with their MJML lost.
   */
  useEffect(() => {
    const source = editSeries ?? duplicateOf;
    if (!source) return;
    setExpectedVersion(undefined);
    let live = true;
    setMsg("Loading the campaign to duplicate…");
    api
      .campaignContent(org, source)
      .then((body) => {
        if (!live) return;
        if (editSeries) {setCampaignId(editSeries);setWhen("recurring");setExpectedVersion(body.version);}
        setSubject(body.subject);
        setPreviewText(body.previewText ?? "");
        if (body.listId) setListId(body.listId);
        setSegmentId(body.segmentId ?? "");
        setFeedId(body.feedId ?? "");
        if (body.newsletter) setNewsletter(body.newsletter);
        if ("html" in body.template) setHtml(body.template.html);
        else if ("blocks" in body.template) {
          setHtml(body.template.blocks.map(b => b.kind === "editorial"
            ? `<a href="${escapeHtml(b.url)}">${escapeHtml(b.label)}</a>` : b.html).join("\n"));
        }
        setMsg(editSeries ? "Edit the current content and policy. The existing schedule is preserved." : `Duplicated from "${duplicateOf}". Give it a new campaign id and schedule it.`);
      })
      .catch((e) => {
        if (!live) return;
        // A campaign scheduled before bodies were stored has none. Say so
        // plainly — "not found" would read as data loss, and the operator can
        // still compose from scratch.
        setMsg(
          String(e).includes("predates-body-storage")
            ? `"${duplicateOf}" was scheduled before bodies were kept, so its content cannot be loaded. Compose it again here.`
            : String(e),
        );
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateOf, editSeries, org]);

  const bodyValid = html.trim() !== "";
  // The server refuses these too (#365); checked here so the operator is told
  // next to the field instead of by a 400 after pressing Schedule.
  const subjectTagged = schemas.subjectHasMergeTag(subject);
  /**
   * The zone an empty Timezone resolves to (#355). The server schedules against
   * the org's `defaultTimezone` and only falls back to UTC when there is none,
   * so the option, the summary and the preview all name the zone it will use.
   * Undefined until the org has loaded, rather than a UTC guess.
   */
  const orgZone = orgMeta.data ? (orgMeta.data.defaultTimezone || "UTC") : undefined;
  const effectiveZone = timezone || orgZone || "";
  const valid =
    (!editSeries || expectedVersion !== undefined) && !!listId && isValidId(campaignId.trim()) && subject.trim() !== "" && !subjectTagged && bodyValid &&
    (when !== "at" || at !== "") && (when !== "recurring" || cron.trim() !== "");

  const htmlTemplates = (templates.data ?? []).filter((t) => t.mode === "raw_html");

  const submit = async () => {
    setMsg(""); setBusy(true);
    try {
      const whenPayload: ScheduleWhen =
        when === "now" ? { type: "now" }
        : when === "at" ? { type: "at", at: new Date(at).toISOString() }
        : { type: "recurring", cron: cron.trim(), ...(timezone.trim() ? { timezone: timezone.trim() } : {}) };
      const template = { html };
      const editorSource = { mode: "html" as const };
      // Feed-only settings are hidden while no feed is picked, so a value left
      // over from before the feed was cleared must not reach the server: section
      // feeds and the WordPress subject both need a feed and would be refused.
      const policy = feedId ? withoutUnplaceableSlots(newsletter, positions) : withoutFeedOnlySettings(newsletter);
      const payload = { orgId: org, campaignId: campaignId.trim(), listId, subject, ...(previewText.trim() ? { previewText: previewText.trim() } : {}), template, editorSource, when: whenPayload, ...(segmentId ? { segmentId } : {}), ...(when === "recurring" ? { newsletter: policy, ...(feedId ? { feedId } : {}) } : {}) };
      if (editSeries) {
        const {when: ignoredWhen,...content} = payload;
        const res = await api.updateNewsletterContent({...content,expectedVersion:expectedVersion!});
        setExpectedVersion(res.version);setMsg(`Saved "${res.scheduleId}".`);
      } else {
        const res = await api.scheduleCampaign(payload);
        setMsg(`Scheduled "${res.scheduleId}" (${res.status}${res.at ? ` · ${new Date(res.at).toLocaleString()}` : ""}${res.timezone ? ` · ${res.timezone}` : ""}).`);
      }
      onScheduled();
    } catch (e) {
      setMsg(String(e));
    } finally {
      setBusy(false);
    }
  };

  const canSendTest = !!listId && subject.trim() !== "" && !subjectTagged && bodyValid && testTo.trim() !== "";
  const sendTest = async () => {
    setTestMsg("");
    setTestBusy(true);
    try {
      const to = testTo
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await api.sendTestEmail({
        orgId: org,
        listId,
        to,
        subject: subject.trim(),
        ...(previewText.trim() ? { previewText: previewText.trim() } : {}),
        template: { html },
      });
      setTestMsg(`Queued "${res.subject}" to ${res.recipients.join(", ")}.`);
    } catch (e) {
      setTestMsg(String(e));
    } finally {
      setTestBusy(false);
    }
  };

  return (
    <div>
      <h1 className="h1">{editSeries ? "Edit newsletter series" : "Compose & schedule"} · {org || "—"}</h1>
      <p className="muted" style={{ marginTop: -8 }}>
        Build a send and schedule it now, at a time, or on a recurring cron. It appears under
        Schedules where you can pause or archive it.
      </p>
      {lists.data && lists.data.length === 0 && (
        <div className="card muted">No newsletters yet — create a list first.</div>
      )}
      <div className="card">
        <label>Newsletter</label>
        <select value={listId} onChange={(e) => setListId(e.target.value)} style={{ width: "100%" }}>
          {(lists.data ?? []).map((l) => (
            <option key={l.listId} value={l.listId}>{l.name} ({l.listId})</option>
          ))}
        </select>
        <label style={{ marginTop: 12 }}>Segment (optional — targets within the list)</label>
        <select value={segmentId} onChange={(e) => setSegmentId(e.target.value)} style={{ width: "100%" }}>
          <option value="">Whole list (no segment)</option>
          {(segments.data ?? []).map((s) => (
            <option key={s.segmentId} value={s.segmentId}>{s.name} ({s.segmentId})</option>
          ))}
        </select>
        {segmentId && (
          <p className="muted" style={{ margin: "6px 0 0" }}>
            Only members of this segment who are <strong>confirmed on {listId || "the list"}</strong> will
            be sent to — segment membership is not consent.
          </p>
        )}
        <label style={{ marginTop: 12 }}>Campaign id</label>
        <input disabled={!!editSeries} value={campaignId} onChange={(e) => setCampaignId(e.target.value)} placeholder="e.g. daily-2026-07-21" style={{ width: "100%" }} />
        {/* Without this the Schedule button just stays disabled, with no hint
            that e.g. a capital letter in the id is the reason. */}
        {idProblem(campaignId.trim()) && <p className="err" style={{ margin: "6px 0 0" }}>Campaign id {idProblem(campaignId.trim())}.</p>}
        <label style={{ marginTop: 12 }}>Subject</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line" style={{ width: "100%" }} />
        {/* The preheader below IS merged, so assuming the subject is too was
            reasonable — and wrong: it goes out exactly as typed (#365). */}
        {subjectTagged && <p className="err" style={{ margin: "6px 0 0" }}>{schemas.SUBJECT_MERGE_TAG_MESSAGE}</p>}
        <label style={{ marginTop: 12 }}>Preview text (optional)</label>
        <input
          value={previewText}
          onChange={(e) => setPreviewText(e.target.value)}
          placeholder="Shown beside the subject in the inbox"
          maxLength={200}
          style={{ width: "100%" }}
        />
        <p className="muted" style={{ margin: "4px 0 0", fontSize: 12 }}>
          {/* Set it or the client picks for you, and what it picks is usually
              the first words of the body or an unsubscribe line. */}
          Most clients show the first 100–150 characters. Leave it blank and the
          client scrapes the top of the email instead.
        </p>
      </div>

      <div className="card">
        <label>HTML / CSS email source</label>
        <p className="muted">Paste the HTML exported from Beefree, including its CSS and newsletter story markers.</p>
          <div>
            {htmlTemplates.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <label>Load a saved HTML template (copy)</label>
                <select
                  defaultValue=""
                  onChange={(e) => {
                    const t = htmlTemplates.find((x) => x.templateId === e.target.value);
                    if (t) setHtml(t.source);
                  }}
                  style={{ width: "100%" }}
                >
                  <option value="" disabled>Choose a template…</option>
                  {htmlTemplates.map((t) => (
                    <option key={t.templateId} value={t.templateId}>{t.name} ({t.templateId})</option>
                  ))}
                </select>
              </div>
            )}
            <textarea value={html} onChange={(e) => setHtml(e.target.value)} rows={12}
              placeholder={"<h1>Hello {{first_name}}</h1>\n<a href=\"https://…\">Read more</a>"}
              style={{ width: "100%", fontFamily: "monospace" }} />
            <p className="muted" style={{ margin: "6px 0 0" }}>
              Email layout and story markers are preserved. Editorial links are tracked; advertiser links keep their original markup.
            </p>
          </div>
      </div>

      <div className="card">
        <div className="muted" style={{ marginBottom: 8 }}>When</div>
        <div style={{ display: "flex", gap: 16 }}>
          {(["now", "at", "recurring"] as const).map((w) => (
            <label key={w} style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input disabled={!!editSeries} type="radio" name="when" checked={when === w} onChange={() => setWhen(w)} />
              {w === "now" ? "Send now" : w === "at" ? "At a time" : "Recurring"}
            </label>
          ))}
        </div>
        {when === "at" && (
          <div style={{ marginTop: 10 }}>
            <label>Send at (your local time; a 5-minute floor always applies)</label>
            <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} style={{ width: "100%" }} />
          </div>
        )}
        {when === "recurring" && (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 12 }}>
            {!editSeries && <>
            <div>
              <label htmlFor="frequency-select" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Frequency</label>
              <select
                id="frequency-select"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as RecurringFrequency)}
                style={{ width: "100%", padding: "6px 12px", borderRadius: 4, border: "1px solid #ccc" }}
              >
                <option value="daily">Daily</option>
                <option value="weekdays">Weekdays (Mon-Fri)</option>
                <option value="weekends">Weekends (Sat-Sun)</option>
                <option value="custom">Custom Days</option>
                {orgMeta.data?.hourlyEnabled && <option value="hourly">Hourly (Testing/Debug)</option>}
              </select>
            </div>

            {frequency === "custom" && (
              <div>
                <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>Days of Week</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {DAYS_OF_WEEK.map((day) => {
                    const active = selectedDays.includes(day.id);
                    return (
                      <button
                        key={day.id}
                        type="button"
                        aria-pressed={active}
                        onClick={() => {
                          setSelectedDays((prev) =>
                            prev.includes(day.id)
                              ? prev.filter((d) => d !== day.id)
                              : [...prev, day.id]
                          );
                        }}
                        style={{
                          padding: "6px 12px",
                          borderRadius: 20,
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: "pointer",
                          border: active ? "1px solid #1b7a3d" : "1px solid #ccc",
                          backgroundColor: active ? "#d7f0df" : "#fbfbfb",
                          color: active ? "#1b7a3d" : "#555",
                          transition: "all 0.15s ease-in-out",
                        }}
                      >
                        {day.shortLabel}
                      </button>
                    );
                  })}
                </div>
                {selectedDays.length === 0 && (
                  // An empty selection is legal and builds a daily schedule
                  // (see buildEventBridgeCron). Say so here, next to the chips
                  // the operator is still clicking — the summary below reads
                  // "Every day", which is easy to miss mid-selection.
                  <p
                    role="status"
                    style={{ marginTop: 6, marginBottom: 0, fontSize: 13, color: "#92400e" }}
                  >
                    No days selected — this will send <strong>every day</strong>. Pick at least one day to limit it.
                  </p>
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Send Time</label>
                <input
                  type="time"
                  value={timeOfDay}
                  onChange={(e) => setTimeOfDay(e.target.value)}
                  style={{ width: "100%", padding: "6px 12px", borderRadius: 4, border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ flex: 1 }}>
                <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Timezone</label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  style={{ width: "100%", padding: "6px 12px", borderRadius: 4, border: "1px solid #ccc" }}
                >
                  <option value="">{orgZone ? `Org default (${orgZone})` : "Org default (loading…)"}</option>
                  {/* An org zone outside the list is still pickable by name, so
                      the operator can pin it explicitly. */}
                  {orgZone && !ZONE_OPTIONS.some((o) => o.zone === orgZone) && (
                    <option value={orgZone}>{orgZone}</option>
                  )}
                  {ZONE_OPTIONS.map((o) => (
                    <option key={o.zone} value={o.zone}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{
              marginTop: 6,
              padding: "10px 14px",
              backgroundColor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 6,
              fontSize: 14,
              color: "#166534",
              display: "flex",
              flexDirection: "column",
              gap: 8
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <strong>📅 Summary:</strong> {describeSchedule({ frequency, daysOfWeek: selectedDays, timeOfDay }, effectiveZone)}
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => {
                    const runs = getNextRuns({ frequency, daysOfWeek: selectedDays, timeOfDay }, effectiveZone);
                    setNextRuns({ runs, zone: effectiveZone });
                  }}
                  style={{
                    backgroundColor: "#166534",
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    padding: "4px 8px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer"
                  }}
                >
                  🔍 Preview Next 60 Runs
                </button>
              </div>
            </div>

            {nextRuns && (
              <div style={{
                maxHeight: 180,
                overflowY: "auto",
                backgroundColor: "#f4f4f5",
                border: "1px solid #e4e4e7",
                borderRadius: 6,
                padding: "10px 14px",
                fontSize: 12,
                color: "#3f3f46"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, borderBottom: "1px solid #ddd", paddingBottom: 4 }}>
                  {/* In the sending zone, not the browser's: "09:00" in the form
                      should read 09:00 here (#355). */}
                  <strong>Calculated Run Times ({nextRuns.zone || "UTC"}):</strong>
                  <span style={{ cursor: "pointer", color: "#ef4444", fontWeight: 600 }} onClick={() => setNextRuns(null)}>✕ Close</span>
                </div>
                <ol style={{ margin: 0, paddingLeft: 20 }}>
                  {nextRuns.runs.map((r, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      {r.toLocaleString(undefined, { timeZone: nextRuns.zone || "UTC" })}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div>
              <details style={{ fontSize: 12, cursor: "pointer", marginTop: 4 }}>
                <summary style={{ color: "#666", outline: "none" }}>Advanced: View generated cron</summary>
                <div style={{
                  marginTop: 6,
                  padding: 8,
                  backgroundColor: "#f4f4f5",
                  border: "1px solid #e4e4e7",
                  borderRadius: 4,
                  fontFamily: "monospace"
                }}>
                  {cron}
                </div>
              </details>
            </div>

            </>}
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Article feed (optional)</label>
              <select value={feedId} onChange={(e) => setFeedId(e.target.value)} style={{ width: "100%", padding: "6px 12px", borderRadius: 4, border: "1px solid #ccc" }}>
                <option value="">Use the composed body</option>
                {(feeds.data ?? []).filter((feed) => feed.targetListId === listId).map((feed) => <option key={feed.feedId} value={feed.feedId}>{feed.feedId} ({feed.format})</option>)}
              </select>
              <p className="muted" style={{ marginTop: 4 }}>{feedId
                ? "Each firing fetches this feed and builds the edition from its latest stories. The settings below control how."
                : "Every firing sends the body composed above. Pick a feed to build each edition from your site's latest stories instead; the feed settings appear once one is picked."}</p>
              {/* These only act on feed stories; with the composed body they did
                  nothing, so they appear once a feed is picked. */}
              {feedId && <>
              {(["categoryOne", "categoryTwo"] as const).map((section, i) => <div key={section}>
                <label>Section {i + 1} feed<select value={newsletter[`${section}FeedId`] ?? ""} onChange={e => setNewsletter(n => ({...n,[`${section}FeedId`]:e.target.value || undefined}))}>
                  <option value="">No separate section</option>{(feeds.data ?? []).filter(f => f.targetListId === listId).map(f => <option key={f.feedId} value={f.feedId}>{f.feedId}</option>)}
                </select></label>
                <p className="muted" style={{marginTop:2}}>Optional. Adds a second block of stories from another feed (e.g. Sports), below the main stories. Needs a template with story regions.</p>
                <label>Section {i + 1} heading<input value={newsletter[`${section}Name`] ?? ""} onChange={e => setNewsletter(n => ({...n,[`${section}Name`]:e.target.value}))} /></label>
                <p className="muted" style={{marginTop:2}}>The heading readers see above this section.</p>
              </div>)}
              </>}
              <label>Last send date (optional)<input type="date" value={newsletter.endDate ?? ""} onChange={e => setNewsletter(n => ({...n,endDate:e.target.value || undefined}))} /></label>
              <p className="muted" style={{marginTop:2}}>After this date (in the schedule's time zone) nothing more is sent. Leave blank to run indefinitely.</p>
              {feedId && <>
              <label>Feed failure<select value={newsletter.failurePolicy} onChange={e => setNewsletter(n => ({...n,failurePolicy:e.target.value as typeof n.failurePolicy}))}><option value="halt">Hold the send if any source fails</option><option value="allow-partial">Send available stories; omit failed secondary sections</option></select></label>
              <p className="muted" style={{marginTop:2}}>When a feed can't be reached or comes back empty. Holding sends nothing that day; the other choice drops a failed Section 1 or 2 and sends the rest. If the main feed fails, the send is always held.</p>
              <label>Unchanged stories<select value={newsletter.unchangedPolicy} onChange={e => setNewsletter(n => ({...n,unchangedPolicy:e.target.value as typeof n.unchangedPolicy}))}><option value="hold">Hold until the editor updates the slots</option><option value="warn">Send and record a warning</option><option value="allow">Allow repeated stories</option></select></label>
              <p className="muted" style={{marginTop:2}}>When the feed's stories are identical to the previous edition's, e.g. nothing new was published.</p>
              <label>Subject<select value={newsletter.subjectMode} onChange={e => setNewsletter(n => ({...n,subjectMode:e.target.value as typeof n.subjectMode}))}><option value="lead">Lead story headline</option><option value="last">Last story headline</option><option value="newsletter">WordPress newsletter subject</option><option value="fixed">Use the composed subject</option></select></label>
              <p className="muted" style={{marginTop:2}}>Where each edition's subject line comes from: the first or last story's headline, a subject your WordPress feed supplies, or the subject typed above.</p>
              </>}
              <details><summary>Legacy template ad placements</summary>
                <p className="muted">Settings carried over from the Pinpoint newsletters, for templates and ad tags built for them.</p>
                <label>Newsletter display name<input value={newsletter.displayName ?? ""} onChange={e => setNewsletter(n => ({...n,displayName:e.target.value || undefined}))} /></label>
                <p className="muted" style={{marginTop:2}}>Fills the template's newsletter-name tag, is the utm_campaign on story links, and builds the placement id ad tags report under. Blank uses the series name.</p>
                <label>Ad audience ID<input value={newsletter.adAudienceId ?? ""} onChange={e => setNewsletter(n => ({...n,adAudienceId:e.target.value || undefined}))} /></label>
                <p className="muted" style={{marginTop:2}}>Replaces {"{LIST_ID}"} in your ad tags, so the ad server knows which audience it is serving. Blank uses the segment, then the newsletter id.</p>
                {/* Slots are placed only into story regions a feed fills; with the
                    composed body they placed nothing and could only hold the send. */}
                {feedId && <>
                <p className="muted">Use the slot IDs configured under Ad tags. Which positions are offered depends on the markers in the HTML above.</p>
                {/* Positions are read off the body being edited: the send fills
                    only markers that exist, so a slot for a missing position
                    would be dropped without a word. */}
                {positions.marquee
                  ? <><label>Marquee slot<input value={newsletter.marqueeSlot ?? ""} onChange={e => setNewsletter(n => ({...n,marqueeSlot:e.target.value || undefined}))} /></label>
                    <p className="muted" style={{marginTop:2}}>The ad at the {"{{MARQUEEAD}}"} marker, at the top of the email.</p></>
                  : <MissingPosition slot="Marquee slot" what="a marquee ad" marker="{{MARQUEEAD}} marker" stale={newsletter.marqueeSlot} />}
                {positions.footer
                  ? <><label>Footer slot<input value={newsletter.footerSlot ?? ""} onChange={e => setNewsletter(n => ({...n,footerSlot:e.target.value || undefined}))} /></label>
                    <p className="muted" style={{marginTop:2}}>The ad at the {"{{SAFERTB}}"} marker, at the bottom of the email.</p></>
                  : <MissingPosition slot="Footer slot" what="a footer ad" marker="{{SAFERTB}} marker" stale={newsletter.footerSlot} />}
                {positions.body
                  ? <><label>Body slots (comma-separated)<input value={newsletter.bodySlots.join(",")} onChange={e => setNewsletter(n => ({...n,bodySlots:e.target.value.split(",").map(s => s.trim()).filter(Boolean)}))} /></label>
                    <p className="muted" style={{marginTop:2}}>Ads placed one after each main story, in this order. A slot named here with no fill under Ad tags holds the send.</p></>
                  : <MissingPosition slot="Body slots" what="body ads" marker="MAIN STORY / BODY STORY regions" stale={newsletter.bodySlots.join(", ") || undefined} />}
                </>}
              </details>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <label htmlFor="test-email-to">Send test email (1–5 comma-separated addresses)</label>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
          <input
            id="test-email-to"
            type="text"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="editor@example.com, desk@example.com"
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn ghost"
            disabled={!canSendTest || testBusy}
            onClick={() => void sendTest()}
          >
            {testBusy ? "Sending test…" : "Send test email"}
          </button>
        </div>
        {testMsg && (
          <p className={testMsg.startsWith("Queued") ? "muted" : "err"} style={{ margin: "6px 0 0" }}>
            {testMsg}
          </p>
        )}
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button className="btn" disabled={!valid || busy} onClick={submit}>
          {busy ? (editSeries ? "Saving…" : "Scheduling…") : (editSeries ? "Save series content" : "Schedule")}
        </button>
        {msg && <span className={msg.startsWith("Scheduled") ? "muted" : "err"}>{msg}</span>}
      </div>
    </div>
  );
}

/**
 * The policy as saved with no article feed: drops what only a feed can use —
 * section feeds, ad slots (placed only into feed story regions, yet a slot with
 * no fill still holds the send) and the WordPress subject.
 */
function withoutFeedOnlySettings(policy: NewsletterPolicy): NewsletterPolicy {
  const { categoryOneFeedId: _c1, categoryTwoFeedId: _c2, categoryOneName: _n1, categoryTwoName: _n2,
    marqueeSlot: _m, footerSlot: _f, ...rest } = policy;
  // Without a feed, "lead" falls back to the composed subject, as the WordPress
  // subject cannot.
  return { ...rest, bodySlots: [], subjectMode: rest.subjectMode === "newsletter" ? "lead" : rest.subjectMode };
}

/** Drops ad slots whose position the HTML lacks; the server refuses them. */
function withoutUnplaceableSlots(policy: NewsletterPolicy, at: AdPositions): NewsletterPolicy {
  const { marqueeSlot, footerSlot, ...rest } = policy;
  return {
    ...rest,
    ...(at.marquee && marqueeSlot ? { marqueeSlot } : {}),
    ...(at.footer && footerSlot ? { footerSlot } : {}),
    bodySlots: at.body ? policy.bodySlots : [],
  };
}

/** In place of a slot input whose ad position the HTML does not have. */
function MissingPosition({ slot, what, marker, stale }: { slot: string; what: string; marker: string; stale?: string }) {
  return stale
    ? <p className="err">{slot} <code>{stale}</code> is set, but this HTML has no {marker}. It will be removed when you save; add the marker to keep it.</p>
    : <p className="muted">This HTML has no {marker}, so it can't carry {what}.</p>;
}
